import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Grid,
  FormControl,
  Select,
  MenuItem,
  InputAdornment,
  IconButton,
  Chip,
  Tabs,
  Tab,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Checkbox,
  FormGroup,
  Switch,
  InputLabel,
} from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DeleteIcon from '@mui/icons-material/Delete';
import ApartmentIcon from '@mui/icons-material/Apartment';
import HomeIcon from '@mui/icons-material/Home';
import BusinessIcon from '@mui/icons-material/Business';
import HolidayVillageIcon from '@mui/icons-material/HolidayVillage';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import UnitManagement from '../../components/landlord/UnitManagement';
import RoomManagement from '../../components/landlord/RoomManagement';
import BedManagement from '../../components/landlord/BedManagement';
import { useNavigate, useParams } from 'react-router-dom';
import { rentalAPI } from '../../utils/api';

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

const PROPERTY_TYPES = [
  'Apartment',
  'House',
  'Townhouse',
  'Condo'
];

const RENTAL_TYPES = [
  'Entire Property',
  'By Room',
  'By Bed'
];

const AMENITIES = [
  'Furnished',
  'In-unit Laundry',
  'Laundry',
  'Dishwasher',
  'Air Conditioning',
  'Heating',
  'Parking',
  'Gym',
  'Pool',
  'Pet Friendly',
  'Utilities Included',
  'Internet Included',
];

const LEASE_LENGTHS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: '3months', label: '3 Months' },
  { value: '6months', label: '6 Months' },
  { value: 'yearly', label: '1 Year' }
];

const LANGUAGES = [
  'English',
  'Chinese',
  'Spanish',
  'French',
  'German',
  'Japanese',
  'Korean',
  'Russian',
  'Arabic',
  'Portuguese',
  'Italian',
  'Hindi',
  'Vietnamese',
  'Thai',
  'Other'
];

const initialFormData = {
  title: '',
  description: '',
  propertyType: '',
  rentalType: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  zipCode: '',
  securityDeposit: '',
  leaseLengths: [],
  availableDate: '',
  selectedAmenities: [],
  images: [],
  contactInfo: {
    email: '',
    responseTime: 'within-2-3-days',
    languages: ['English']
  },
  units: [],
  rooms: [],
  beds: [],
  hasPromotion: false,
  promotionText: '',
  price: '',
  totalBedrooms: '',
  totalBathrooms: '',
  squareFeet: '',
  youtubeVideoUrls: ['', '', ''],
  matterportUrls: ['', '', ''],
};

function LandlordPostForm() {
  const navigate = useNavigate();
  const params = useParams();
  const [formData, setFormData] = useState(initialFormData);
  const [isEditMode, setIsEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);

  // 检测是否为编辑模式
  useEffect(() => {
    const editId = params.id;
    if (editId) {
      setIsEditMode(true);
      setLoading(true);
      loadExistingListing(editId);
    }
  }, [params.id]);

  // 编辑模式下初始化图片URL
  useEffect(() => {
    if (isEditMode && formData.images) {
      // 兼容后端返回的字符串数组或对象数组
      setUploadedImages(formData.images.map(img => typeof img === 'string' ? img : img.url || img.preview));
    }
  }, [isEditMode, formData.images]);

  // 模拟加载现有房源数据的函数
  const loadExistingListing = async (id) => {
    try {
      // 调用后端API获取房源详情
      const res = await rentalAPI.getRental(id);
      const data = res.data;
      setFormData({
        title: data.title || '',
        description: data.description || '',
        propertyType: data.property_type || '',
        rentalType: (data.rental_type === 'entire' ? 'Entire Property' : data.rental_type === 'room' ? 'By Room' : 'By Bed'),
        addressLine1: data.address_line1 || '',
        addressLine2: data.address_line2 || '',
        city: data.city || '',
        state: data.state || '',
        zipCode: data.zip_code || '',
        securityDeposit: data.security_deposit || '',
        leaseLengths: data.lease_lengths || [],
        availableDate: data.available_date || '',
        selectedAmenities: data.amenities || [],
        images: data.images || [],
        contactInfo: data.contact_info || {
          email: '',
          responseTime: 'within-2-3-days',
          languages: ['English']
        },
        units: data.units || [],
        rooms: data.rooms || [],
        beds: data.beds || [],
        hasPromotion: data.has_promotion || false,
        promotionText: data.promotion_text || '',
        price: data.price || '',
        totalBedrooms: data.bedrooms || '',
        totalBathrooms: data.bathrooms || '',
        squareFeet: data.square_feet || '',
        youtubeVideoUrls: data.youtube_video_urls && data.youtube_video_urls.length > 0
          ? [...data.youtube_video_urls, ...['', '', '']].slice(0, 3)
          : ['', '', ''],
        matterportUrls: data.matterport_urls && data.matterport_urls.length > 0
          ? [...data.matterport_urls, ...['', '', '']].slice(0, 3)
          : ['', '', ''],
      });
    } catch (error) {
      console.error('Error loading listing:', error);
      // TODO: 显示错误消息
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleContactInfoChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      contactInfo: {
        ...prev.contactInfo,
        [name]: value
      }
    }));
  };

  const handleAmenityToggle = (amenity) => {
    setFormData(prev => {
      const currentAmenities = prev.selectedAmenities || [];
      return {
        ...prev,
        selectedAmenities: currentAmenities.includes(amenity)
          ? currentAmenities.filter(item => item !== amenity)
          : [...currentAmenities, amenity]
      };
    });
  };

  const handleYoutubeUrlChange = (index, value) => {
    setFormData(prev => {
      const newUrls = [...prev.youtubeVideoUrls];
      newUrls[index] = value;
      return {
        ...prev,
        youtubeVideoUrls: newUrls
      };
    });
  };

  const handleMatterportUrlChange = (index, value) => {
    setFormData((prev) => {
      const newUrls = [...prev.matterportUrls];
      newUrls[index] = value;
      return { ...prev, matterportUrls: newUrls };
    });
  };

  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    for (const file of files) {
      try {
        const res = await rentalAPI.uploadRentalImage(file);
        const { url } = res.data;
        setUploadedImages(prev => [...prev, url]);
      } catch (err) {
        alert('Failed to upload image.');
      }
    }
  };

  const handleImageDelete = (index) => {
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
  };

  const handlePropertyTypeChange = (event) => {
    setFormData(prev => ({
      ...prev,
      propertyType: event.target.value
    }));
  };

  const handleRentalTypeChange = (event) => {
    setFormData(prev => ({
      ...prev,
      rentalType: event.target.value
    }));
  };

  const handleLeaseLengthToggle = (length) => {
    setFormData(prev => {
      const currentLengths = prev.leaseLengths || [];
      return {
        ...prev,
        leaseLengths: currentLengths.includes(length)
          ? currentLengths.filter(item => item !== length)
          : [...currentLengths, length]
      };
    });
  };

  const handleLanguageToggle = (language) => {
    setFormData(prev => ({
      ...prev,
      contactInfo: {
        ...prev.contactInfo,
        languages: (prev.contactInfo.languages || []).includes(language)
          ? prev.contactInfo.languages.filter(l => l !== language)
          : [...(prev.contactInfo.languages || []), language]
      }
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const rentalTypeMap = {
        'Entire Property': 'entire',
        'By Room': 'room',
        'By Bed': 'bed',
      };
      const isSpecialEntire =
        (formData.propertyType === 'House' ||
         formData.propertyType === 'Townhouse' ||
         formData.propertyType === 'Condo');
      
      // 清理beds和rooms数组中的bedrooms和bathrooms字段（对于非Apartment类型）
      let cleanedBeds = formData.beds;
      let cleanedRooms = formData.rooms;
      if (isSpecialEntire && formData.propertyType !== 'Apartment') {
        cleanedBeds = formData.beds.map(bed => {
          const { bedrooms, bathrooms, ...cleanedBed } = bed;
          return cleanedBed;
        });
        cleanedRooms = formData.rooms.map(room => {
          const { bedrooms, bathrooms, ...cleanedRoom } = room;
          return cleanedRoom;
        });
      }

      const resolveTopLevelBedBath = () => {
        if (isSpecialEntire) {
          return {
            bedrooms: parseInt(formData.totalBedrooms, 10) || 0,
            bathrooms: parseFloat(formData.totalBathrooms) || 0,
          };
        }
        if (formData.rentalType === 'By Room' && formData.rooms?.length) {
          const pick =
            formData.rooms.find((r) => r.isAvailable !== false) || formData.rooms[0];
          return {
            bedrooms: parseInt(pick?.bedrooms, 10) || 0,
            bathrooms: parseFloat(pick?.bathrooms) || 0,
          };
        }
        if (formData.rentalType === 'By Bed' && formData.beds?.length) {
          const pick =
            formData.beds.find((b) => b.isAvailable !== false) || formData.beds[0];
          return {
            bedrooms: parseInt(pick?.bedrooms, 10) || 0,
            bathrooms: parseFloat(pick?.bathrooms) || 0,
          };
        }
        if (formData.units?.length) {
          const pick =
            formData.units.find((u) => u.isAvailable !== false) || formData.units[0];
          return {
            bedrooms: parseInt(pick?.bedrooms, 10) || 0,
            bathrooms: parseFloat(pick?.bathrooms) || 0,
          };
        }
        return { bedrooms: 0, bathrooms: 0 };
      };
      const topLevelBedBath = resolveTopLevelBedBath();
      
      const payload = {
        title: formData.title,
        description: formData.description,
        property_type: formData.propertyType,
        rental_type: rentalTypeMap[formData.rentalType] || 'entire',
        address_line1: formData.addressLine1,
        address_line2: formData.addressLine2,
        city: formData.city,
        state: formData.state,
        zip_code: formData.zipCode,
        price: isSpecialEntire ? 
          parseFloat(formData.price) || 0 : 
          parseFloat(formData.units[0]?.price) || 0,
        bedrooms: topLevelBedBath.bedrooms,
        bathrooms: topLevelBedBath.bathrooms,
        square_feet: isSpecialEntire
          ? (formData.squareFeet !== undefined && formData.squareFeet !== '' ? parseInt(formData.squareFeet, 10) : null)
          : null,
        amenities: formData.selectedAmenities,
        images: uploadedImages,
        security_deposit: formData.securityDeposit,
        lease_lengths: formData.leaseLengths,
        available_date: formData.availableDate
          ? (typeof formData.availableDate === 'string'
              ? formData.availableDate.slice(0, 10)
              : formData.availableDate.toISOString().slice(0, 10))
          : null,
        contact_info: formData.contactInfo,
        units: isSpecialEntire && formData.propertyType === 'Apartment' ? [] : formData.units,
        rooms: cleanedRooms,
        beds: cleanedBeds,
        has_promotion: formData.hasPromotion,
        promotion_text: formData.promotionText,
        youtube_video_urls: formData.youtubeVideoUrls.filter((url) => url.trim() !== ''),
        matterport_urls: formData.matterportUrls.filter((url) => url.trim() !== ''),
      };
      if (isEditMode && params.id) {
        await rentalAPI.updateRental(params.id, payload);
        alert('Rental updated successfully!');
      } else {
        await rentalAPI.createRental(payload);
        alert('Rental posted successfully!');
      }
      navigate('/landlord/profile');
    } catch (error) {
      console.error('Submit error:', error);
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message || 
                          error.message || 
                          'Failed to submit rental.';
      alert(`Failed to submit rental: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(-1); // Go back to previous page
  };

  // Helper function to determine which management component to show
  const renderManagementComponent = () => {
    if (!formData.propertyType || !formData.rentalType) return null;

    // Wrap all management components with consistent spacing
    const wrapWithSpacing = (component) => (
      <Box sx={{ mt: 2, mb: 3 }}>
        {component}
      </Box>
    );

    if (formData.propertyType === 'Apartment') {
      switch (formData.rentalType) {
        case 'Entire Property':
          return wrapWithSpacing(
            <UnitManagement
              units={formData.units}
              onUnitsChange={(units) => setFormData(prev => ({ ...prev, units }))}
            />
          );
        case 'By Room':
          return (
            <RoomManagement
              rooms={formData.rooms}
              onRoomsChange={(rooms) => setFormData(prev => ({ ...prev, rooms }))}
              propertyType={formData.propertyType}
            />
          );
        case 'By Bed':
          return (
            <BedManagement
              beds={formData.beds}
              onBedsChange={(beds) => setFormData(prev => ({ ...prev, beds }))}
              propertyType={formData.propertyType}
            />
          );
        default:
          return null;
      }
    } else {
      // For House, Townhouse, and Condo
      switch (formData.rentalType) {
        case 'Entire Property':
          return wrapWithSpacing(
            <Grid container spacing={2}>
              {/* 删除Total Bedrooms/Total Bathrooms输入框，只保留其他项 */}
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  required
                  name="squareFeet"
                  label="Square Feet"
                  type="number"
                  value={formData.squareFeet || ''}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  name="price"
                  label="Monthly Rent"
                  type="number"
                  value={formData.price || ''}
                  onChange={handleInputChange}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  name="availableDate"
                  label="Available Date"
                  type="date"
                  value={formData.availableDate || ''}
                  onChange={handleInputChange}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
            </Grid>
          );
        case 'By Room':
          return wrapWithSpacing(
            <RoomManagement
              rooms={formData.rooms}
              onRoomsChange={(rooms) => setFormData(prev => ({ ...prev, rooms }))}
            />
          );
        case 'By Bed':
          return wrapWithSpacing(
            <BedManagement
              beds={formData.beds}
              onBedsChange={(beds) => setFormData(prev => ({ ...prev, beds }))}
            />
          );
        default:
          return null;
      }
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom>
          {isEditMode ? 'Edit Rental Property' : 'Post Rental Property'}
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <Typography>Loading listing data...</Typography>
          </Box>
        ) : (
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
            {/* Property Type Selection */}
            <Box sx={{ mb: 3 }}>
              <FormControl required>
                <FormLabel id="property-type-label">Property Type</FormLabel>
                <RadioGroup
                  row
                  aria-labelledby="property-type-label"
                  name="propertyType"
                  value={formData.propertyType}
                  onChange={handlePropertyTypeChange}
                >
                  {PROPERTY_TYPES.map((type) => (
                    <FormControlLabel
                      key={type}
                      value={type}
                      control={<Radio />}
                      label={type}
                      sx={{
                        mr: 4, // Add more margin to the right
                        '& .MuiFormControlLabel-label': {
                          fontSize: '1rem', // Slightly larger text
                        }
                      }}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </Box>

            {/* Rental Type Selection */}
            <Box sx={{ mb: 3 }}>
              <FormControl required>
                <FormLabel id="rental-type-label">Rental Type</FormLabel>
                <RadioGroup
                  row
                  aria-labelledby="rental-type-label"
                  name="rentalType"
                  value={formData.rentalType}
                  onChange={handleRentalTypeChange}
                >
                  {RENTAL_TYPES.map((type) => (
                    <FormControlLabel
                      key={type}
                      value={type}
                      control={<Radio />}
                      label={type}
                      sx={{
                        mr: 4,
                        '& .MuiFormControlLabel-label': {
                          fontSize: '1rem',
                        }
                      }}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </Box>

            {/* 总几B几B输入框：只要不是Apartment都显示 */}
            {formData.propertyType && formData.propertyType !== 'Apartment' && (
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    name="totalBedrooms"
                    label="Total Bedrooms"
                    type="number"
                    value={formData.totalBedrooms || ''}
                    onChange={handleInputChange}
                    helperText="If it's a studio, please enter 0 bedrooms 1 bathroom."
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    name="totalBathrooms"
                    label="Total Bathrooms"
                    type="number"
                    value={formData.totalBathrooms || ''}
                    onChange={handleInputChange}
                  />
                </Grid>
              </Grid>
            )}
            {/* 管理组件 */}
            {renderManagementComponent()}

            {/* Basic Information Section */}
            <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 3 }}>
              Basic Information
            </Typography>

            {/* Title */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Property Name/Title *
              </Typography>
              <TextField
                required
                fullWidth
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Give your listing a descriptive title"
                variant="outlined"
              />
            </Box>

            {/* Description */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Description *
              </Typography>
              <TextField
                required
                fullWidth
                multiline
                rows={4}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Describe your property"
                variant="outlined"
              />
            </Box>

            {/* Location Information */}
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              Location Information
            </Typography>
            
            {/* Address Line 1 */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Address Line 1 *
              </Typography>
              <TextField
                required
                fullWidth
                name="addressLine1"
                value={formData.addressLine1}
                onChange={handleInputChange}
                placeholder="Enter street address"
                variant="outlined"
              />
            </Box>

            {/* Address Line 2 */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Address Line 2
              </Typography>
              <TextField
                fullWidth
                name="addressLine2"
                value={formData.addressLine2}
                onChange={handleInputChange}
                placeholder="Apartment, suite, unit, etc. (optional)"
                variant="outlined"
              />
            </Box>

            {/* City, State, ZIP on one line */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={5}>
                <Typography variant="subtitle2" gutterBottom>
                  City *
                </Typography>
                <TextField
                  required
                  fullWidth
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  placeholder="Enter city"
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={3}>
                <Typography variant="subtitle2" gutterBottom>
                  State *
                </Typography>
                <FormControl fullWidth required>
                  <Select
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    displayEmpty
                    variant="outlined"
                  >
                    <MenuItem value="" disabled>
                      Select state
                    </MenuItem>
                    {US_STATES.map((state) => (
                      <MenuItem key={state} value={state}>
                        {state}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="subtitle2" gutterBottom>
                  ZIP Code *
                </Typography>
                <TextField
                  required
                  fullWidth
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleInputChange}
                  placeholder="Enter ZIP code"
                  variant="outlined"
                />
              </Grid>
            </Grid>

            {/* Security Deposit */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Security Deposit *
                </Typography>
                <TextField
                  required
                  fullWidth
                  name="securityDeposit"
                  value={formData.securityDeposit}
                  onChange={handleInputChange}
                  placeholder="Enter security deposit amount or policy (e.g., 'One month rent', 'Half of monthly rent')"
                  variant="outlined"
                />
              </Grid>
            </Grid>

            {/* Lease Length Options */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Lease Length Options *
              </Typography>
              <FormGroup>
                <Grid container spacing={2}>
                  {LEASE_LENGTHS.map((option) => (
                    <Grid item xs={6} sm={4} md={3} key={option.value}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={(formData.leaseLengths || []).includes(option.value)}
                            onChange={() => handleLeaseLengthToggle(option.value)}
                          />
                        }
                        label={option.label}
                      />
                    </Grid>
                  ))}
                </Grid>
              </FormGroup>
              {formData.leaseLengths.length === 0 && (
                <Typography color="error" variant="caption" sx={{ display: 'block', mt: 1 }}>
                  Please select at least one lease length option
                </Typography>
              )}
            </Box>

            {/* Contact Information Section */}
            <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 3 }}>
              Contact Information
            </Typography>

            {/* Email */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Email Address *
              </Typography>
              <TextField
                required
                fullWidth
                name="email"
                value={formData.contactInfo.email}
                onChange={handleContactInfoChange}
                placeholder="Enter your email address"
                variant="outlined"
                type="email"
              />
            </Box>

            {/* Response Time */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Typical Response Time *
              </Typography>
              <FormControl fullWidth required>
                <Select
                  name="responseTime"
                  value={formData.contactInfo.responseTime}
                  onChange={handleContactInfoChange}
                  variant="outlined"
                >
                  <MenuItem value="within-same-day">Within same day</MenuItem>
                  <MenuItem value="within-1-day">Within 1 business day</MenuItem>
                  <MenuItem value="within-2-3-days">Within 2-3 business days</MenuItem>
                  <MenuItem value="within-1-week">Within 1 week</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Languages Spoken */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Languages Spoken *
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select all languages you can communicate in
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {LANGUAGES.map((language) => (
                  <Chip
                    key={language}
                    label={language}
                    onClick={() => handleLanguageToggle(language)}
                    color={(formData.contactInfo.languages || []).includes(language) ? 'primary' : 'default'}
                    variant={(formData.contactInfo.languages || []).includes(language) ? 'filled' : 'outlined'}
                  />
                ))}
              </Box>
              {(formData.contactInfo.languages || []).length === 0 && (
                <Typography color="error" variant="caption" sx={{ display: 'block', mt: 1 }}>
                  Please select at least one language
                </Typography>
              )}
            </Box>

            {/* Amenities Section */}
            <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 3 }}>
              Amenities
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {AMENITIES.map((amenity) => (
                  <Chip
                    key={amenity}
                    label={amenity}
                    onClick={() => handleAmenityToggle(amenity)}
                    color={(formData.selectedAmenities || []).includes(amenity) ? 'primary' : 'default'}
                  />
                ))}
              </Box>
            </Box>

            {/* Promotions Section */}
            <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 3 }}>
              Promotions
            </Typography>

            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.hasPromotion}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      hasPromotion: e.target.checked
                    }))}
                  />
                }
                label="Add Promotion"
              />

              {formData.hasPromotion && (
                <Box sx={{ mt: 2 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Promotion Details"
                    value={formData.promotionText}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      promotionText: e.target.value
                    }))}
                    placeholder="Enter promotion details (e.g., '10% off first month rent', 'Sign up now and get $500 off', etc.)"
                  />
                </Box>
              )}
            </Box>

            {/* Property Photos */}
            <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 3 }}>
              Property Photos
            </Typography>

            <Box sx={{ mb: 3 }}>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="property-image-upload"
                type="file"
                multiple
                onChange={handleImageUpload}
              />
              <label htmlFor="property-image-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<PhotoCameraIcon />}
                >
                  Upload Property Photos
                </Button>
              </label>
              <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {uploadedImages.map((url, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      position: 'relative',
                      width: 100,
                      height: 100,
                    }}
                  >
                    <img
                      src={url}
                      alt={`Upload ${idx + 1}`}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: 1,
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleImageDelete(idx)}
                      sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        bgcolor: 'background.paper',
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Video & 3D tour links */}
            <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 3 }}>
              Video & 3D Tours (Optional)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Add up to 3 YouTube videos and up to 3 Matterport 3D tour links. They appear in the same section on the listing page.
            </Typography>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              YouTube
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {formData.youtubeVideoUrls.map((url, index) => (
                <Grid item xs={12} key={`yt-${index}`}>
                  <TextField
                    fullWidth
                    label={`YouTube link ${index + 1}`}
                    placeholder="https://www.youtube.com/watch?v=..."
                    value={url}
                    onChange={(e) => handleYoutubeUrlChange(index, e.target.value)}
                    variant="outlined"
                  />
                </Grid>
              ))}
            </Grid>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Matterport 3D
            </Typography>
            <Grid container spacing={2}>
              {formData.matterportUrls.map((url, index) => (
                <Grid item xs={12} key={`mp-${index}`}>
                  <TextField
                    fullWidth
                    label={`Matterport link ${index + 1}`}
                    placeholder="https://my.matterport.com/show/?m=..."
                    value={url}
                    onChange={(e) => handleMatterportUrlChange(index, e.target.value)}
                    variant="outlined"
                    helperText="Paste the share link from Matterport (my.matterport.com)"
                  />
                </Grid>
              ))}
            </Grid>

            {/* Form Buttons */}
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                variant="outlined"
                color="inherit"
                onClick={handleCancel}
                sx={{ minWidth: 100 }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                sx={{ minWidth: 100 }}
              >
                {isEditMode ? 'Update' : 'Post'}
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
    </Container>
  );
}

export default LandlordPostForm; 